import {ChangeDetectorRef, Component, forwardRef, Input, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {ContracteeRegionService} from '@services/contractee-region.service';
import {ContracteeRegion} from '@models/contractee-region.interface';
import {FuseAlertType} from '@fuse/components/alert';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import {ControlValueAccessor, UntypedFormBuilder, UntypedFormGroup, NG_VALUE_ACCESSOR, Validators} from '@angular/forms';
import {finalize} from 'rxjs';
import {FuseConfirmationService} from '@fuse/services/confirmation';

@Component({
    selector: 'app-region-selector',
    templateUrl: './region-selector.component.html',
    styleUrls: ['./region-selector.component.scss'],
    providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => RegionSelectorComponent),
            multi: true
        }],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class RegionSelectorComponent implements OnInit, ControlValueAccessor {

    @Input() contracteeId!: number;
    @Input() value: number = 0;

    regions: ContracteeRegion[] = [];
    selectedRegionId: number = 0;
    disabled: boolean = false;
    alertGetRegions: { type: FuseAlertType; message: string } = {
        type: 'error',
        message: 'Ocurrio un error al obtener las regiones.'
    };
    alertModifyRegions: { type: FuseAlertType; message: string } = {type: 'info', message: ''};
    form: UntypedFormGroup = this._fb.group({
        regionName: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(100)]]
    });
    regionToModify: ContracteeRegion | null = null;
    modifyRegion: boolean = false;
    loader: boolean = false;
    private readonly alertTime: number = 3000;
    private _showAlertGetRegions: boolean = false;
    private _showAlertModifyRegions: boolean = false;
    private onChange: (value: number | null) => void = (): void => {
    };
    private onTouched: () => void = (): void => {
    };

    get showAlertGetRegions(): boolean {
        return this._showAlertGetRegions;
    }

    set showAlertGetRegions(value: boolean) {
        this._showAlertGetRegions = value;
        this._cdr.markForCheck();
        if (value) {
            setTimeout(() => {
                this._showAlertGetRegions = false;
                this._cdr.markForCheck();
            }, this.alertTime);
        }
    }

    get showAlertModifyRegions(): boolean {
        return this._showAlertModifyRegions;
    }

    set showAlertModifyRegions(value: boolean) {
        this._showAlertModifyRegions = value;
        this._cdr.markForCheck();
        if (value) {
            setTimeout(() => {
                this._showAlertModifyRegions = false;
                this._cdr.markForCheck();
            }, this.alertTime);
        }
    }

    constructor(private readonly _cdr: ChangeDetectorRef,
                private readonly _fuseConfirmationService: FuseConfirmationService,
                private readonly _fb: UntypedFormBuilder,
                private readonly _service: ContracteeRegionService) {
    }

    ngOnInit(): void {
        this.fetchRegions();
    }

    fetchRegions(regionId?: number): void {
        this.loader = true;
        this._service.getContracteeRegions(this.contracteeId).pipe(
            finalize(() => {
                this.loader = false;
            })
        ).subscribe({
            next: (regions: ContracteeRegion[]) => {
                this.regions = regions;
                if (regionId) this.selectedRegionId = regionId;
                this.onChange(this.selectedRegionId);
                this._cdr.markForCheck();
            },
            error: (error: HttpErrorResponse) => {
                console.error('Error fetching regions:', error.message);
                this.showAlertGetRegions = true;
            }
        });
    }

    showRegionNameForm(region: ContracteeRegion): void {
        this.modifyRegion = true;
        this.form.setValue({regionName: region.name});
        this.regionToModify = region;
    }

    addOrUpdateRegion(): void {
        if (this.modifyRegion && this.regionToModify) {
            this.updateRegion();
        } else {
            this.addRegion();
        }
    }

    addRegion(): void {
        if (this.form.valid) {
            this.loader = true;
            const newRegion: ContracteeRegion = {
                id: null,
                contracteeId: this.contracteeId,
                name: this.form.value.regionName
            };

            this._service.createContracteeRegion(newRegion).pipe(
                finalize(() => {
                    this.showAlertModifyRegions = true;
                    this.loader = false;
                })
            ).subscribe({
                next: (region: ContracteeRegion): void => {
                    this.form.reset();
                    this.alertModifyRegions = {type: 'success', message: 'Región agregada exitosamente.'};
                    this.fetchRegions(region.id);
                },
                error: (error: HttpErrorResponse): void => {
                    console.error('Error adding region:', error.message);
                    this.alertModifyRegions = {type: 'error', message: 'Ocurrió un error al agregar la región.'};
                }
            });
        }
    }

    updateRegion(): void {
        if (this.form.valid) {
            this.loader = true;
            this.regionToModify.name = this.form.value.regionName;
            this._service.updateContracteeRegion(this.regionToModify).pipe(
                finalize(() => {
                    this.showAlertModifyRegions = true;
                    this.loader = false;
                })
            ).subscribe({
                next: (updatedRegion: ContracteeRegion): void => {
                    this.form.reset();
                    this.modifyRegion = false;
                    this.alertModifyRegions = {type: 'success', message: 'Región actualizada exitosamente.'};
                    this.fetchRegions(updatedRegion.id);
                },
                error: (error: HttpErrorResponse): void => {
                    console.error('Error updating region:', error.message);
                    this.alertModifyRegions = {type: 'error', message: 'Ocurrió un error al actualizar la región.'};
                }
            });
        }
    }

    deleteRegion(regionId: number): void {
        const confirmation = this._fuseConfirmationService.open({
            title: 'Eliminar Región',
            message: '¿Estás seguro de que deseas eliminar esta región? Para poder elimnarla, debes asegurarte que' +
                ' otros contratos no la tengan seleccionada.',
            actions: {
                confirm: {
                    label: 'Eliminar'
                },
                cancel: {
                    label: 'Cancelar'
                }
            }
        });

        confirmation.afterClosed().subscribe((result) => {
            if (result === 'confirmed') {
                this.loader = true;
                this._service.deleteContracteeRegion(regionId).pipe(
                    finalize(() => {
                        this.showAlertModifyRegions = true;
                        this.loader = false;
                    })
                ).subscribe({
                    next: (): void => {
                        this.alertModifyRegions = {type: 'success', message: 'Región eliminada exitosamente.'};
                        this.fetchRegions(this.selectedRegionId === regionId ? 0 : this.selectedRegionId);
                    },
                    error: (error: HttpErrorResponse): void => {
                        console.error('Error deleting region:', error.message);
                        let message: string = 'Ocurrió un error al eliminar la región.';
                        if (error instanceof HttpErrorResponse && error.status === HttpStatusCode.Conflict) {
                            message = 'No se puede eliminar la región porque está siendo utilizada en otros contratos.';
                        }
                        this.alertModifyRegions = {type: 'error', message};
                    }
                });
            }
        });
    }

    /* ControlValueAccessor Methods */

    writeValue(value: number | null): void {
        this.selectedRegionId = value;
        this._cdr.markForCheck();
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
        this._cdr.markForCheck();
    }

    /********************************/

    onRegionChange(regionId: number | null): void {
        this.selectedRegionId = regionId;
        this.onChange(regionId);
        this.onTouched();
    }

    getSelectedRegionName(): string {
        if (this.selectedRegionId === 0) return 'Sin región';
        if (this.selectedRegionId === -1) return 'Agregar región...';
        return this.regions.find(r => r.id === this.selectedRegionId)?.name || '';
    }
}
